import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import { EyeOutlined } from '@ant-design/icons';
import { getPage } from 'hooks/useUser';
import AcademyCategoriesApi from 'libs/api/retailAcademy/academyCategories';
import { useNavigate } from 'hooks/useNavigate';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { academyCategoriesAdapter } from './academyCategories.adapter';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import { selectYears, selectMarcoArea } from '../../contants';
import LinkWithParams from 'components/linkWithParams';

const api = new AcademyCategoriesApi();

const defaultState = {
  academyCategories: [],
  pagination: {
    current: 1,
    pageSize: 30,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const useAcademyCategories = (isMainPage = true, id) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(defaultState);
  const [loading, setLoading] = useState(false);
  const onChangeTable = useTableChangeFilterHook();
  const { searchParams } = useFilter();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Macro-Area'),
        dataIndex: 'macro_area',
        sorter: () => {},
      },
      {
        title: t('Anno Edizione'),
        dataIndex: 'year',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 180,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            {(pages?.ability?.can_read !== false ||
              row.can_read !== false) && (
              <Col>
                <Tooltip title={t('Visualizza')}>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type='secondary' icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_update !== false && (
              <Col>
                <Tooltip title={t('Modifica')}>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Tooltip title={t('Elimina')}>
                  <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </LinkWithParams>
                </Tooltip>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [t, pathname, pages?.ability?.can_read]);

  const filters = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Titolo'),
        type: 'input',
        name: 'by_title',
        values: [],
        isActiveSearch: true,
      },
      {
        title: t('Macro-Area'),
        type: 'select',
        name: 'by_macro_area',
        values: selectMarcoArea.map((i) => ({
          value: i,
          title: i,
        })),
      },
      {
        title: t('Anno Edizione'),
        type: 'select',
        name: 'by_year',
        values: selectYears.map((i) => ({
          value: i,
          title: i,
        })),
      },
    ];
  }, [t, shoppingCenters]);

  const handleGet = async () => {
    setLoading(true);
    const currentParams = Object.fromEntries([...searchParams]);
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...currentParams,
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { academy_categories = [], pagination = { total_count: 0 } },
      } = await api.list({ params });
      setData((s) => ({
        ...s,
        academyCategories: academyCategoriesAdapter(academy_categories),
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateShop = async (values, id) => {
    setLoading(true);
    try {
      const data = {
        academy_category: {
          ...values,
        },
      };
      if (!id) {
        await api.create({ data });
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.put(id, { data });
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          shopping_center: 'shopping_center_id',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.destroy(id);
      await handleGet();
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      const error = e.response.data.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  useEffect(() => {
    if (isMainPage) {
      (async () => {
        await handleGet();
      })();
    }
  }, [data.pagination.current, data.sorting.order, isMainPage, searchParams]);

  useEffect(() => {
    (async () => {
      if (id) {
        setLoading(true);
        try {
          const {
            data: { academy_category },
          } = await api.get(id);
          let fieldsValues = [];
          for (let key in academy_category) {
            if (key === 'shopping_center') {
              fieldsValues.push({
                name: 'shopping_center_id',
                value: academy_category[key]?.id,
              });
            } else {
              fieldsValues.push({
                name: key,
                value: academy_category[key],
              });
            }
            form.setFields(fieldsValues);
          }
        } catch (e) {
          console.log(e);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [id]);

  return {
    columns,
    data,
    filters,
    loading,
    form,
    onChangeTableFilter,
    shoppingCenters,
    selectYears,
    selectMarcoArea,
    handleCreateOrUpdateShop,
    handleDelete,
  };
};

export default useAcademyCategories;
